import {
    BellIcon,
    BellOffIcon,
    LockIcon,
    LockOpenIcon,
    MoreHorizontalIcon,
    MoveRightIcon,
    PencilIcon,
    PinIcon,
    PinOffIcon,
    Trash2Icon,
} from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { extractStorageResources } from '@campai/lib-common/entities'
import { formatDateTime } from '@campai/lib-common/i18n'
import { Actor, ActorName, Markdown } from '@campai/lib-web/components'
import {
    Badge,
    Button,
    Card,
    CardDescription,
    CardHeader,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    Skeleton,
} from '@campai/lib-web/components/new/ui'
import { type RouterOutput } from '@campai/lib-web/utils/trpc'

import { AttachmentList } from '@/product/community/shared/Attachments'

type TopicData = RouterOutput['product']['community']['topics']['getTopic']

type HeaderProps = {
    topic: TopicData | null
    topicLocked: boolean
    topicPinned: boolean
    isFollowUpdating: boolean
    setTopicFollowed: (followed: boolean) => Promise<void>
    canModerate: boolean
    setTopicPinned: (pinned: boolean) => Promise<void>
    setTopicLocked: (locked: boolean) => Promise<void>
    setMoveDialogOpen: (open: boolean) => void
    canEditTopic: boolean
    setTopicDialogOpen: (open: boolean) => void
    canDeleteTopic: boolean
    setTopicDeleteOpen: (open: boolean) => void
}

export const Header = observer(
    ({
        topic,
        topicLocked,
        topicPinned,
        isFollowUpdating,
        setTopicFollowed,
        canModerate,
        setTopicPinned,
        setTopicLocked,
        setMoveDialogOpen,
        canEditTopic,
        setTopicDialogOpen,
        canDeleteTopic,
        setTopicDeleteOpen,
    }: HeaderProps) => {
        const { i18n, t } = useTranslation()
        const topicCreatedAt = topic?.createdAt ? formatDateTime(topic.createdAt, { i18n }) : ''

        return (
            <Card className={topicLocked ? 'bg-muted/40' : undefined}>
                <CardHeader className="gap-4">
                    <div className="flex items-start gap-4">
                        {topic ? (
                            <Actor size="xl" actor={topic.createdBy!} />
                        ) : (
                            <Skeleton className="size-14 shrink-0 rounded-full" />
                        )}
                        <div className="min-w-0 flex-1 space-y-3">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                                        <div className="truncate text-xl font-medium">
                                            {topic ? (
                                                <ActorName actor={topic.createdBy!} />
                                            ) : (
                                                t('product.community.topic.loading')
                                            )}
                                        </div>
                                        {topicPinned && (
                                            <Badge variant="outline">
                                                <PinIcon className="size-3.5" />
                                                {t('product.community.topic.pinned')}
                                            </Badge>
                                        )}
                                        {topicLocked && (
                                            <Badge variant="outline">
                                                <LockIcon className="size-3.5" />
                                                {t('product.community.topic.locked')}
                                            </Badge>
                                        )}
                                    </div>
                                    <CardDescription>
                                        {topic
                                            ? [
                                                  topicCreatedAt,
                                                  t('product.community.topic.postCount', { posts: topic.postsCount }),
                                              ]
                                                  .filter(Boolean)
                                                  .join(' · ')
                                            : t('product.community.topic.loading')}
                                    </CardDescription>
                                </div>
                                <div className="flex shrink-0 items-center gap-1">
                                    {topic && (
                                        <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            loading={isFollowUpdating}
                                            title={
                                                topic.followedByCurrentUser
                                                    ? t('product.community.topic.unfollow')
                                                    : t('product.community.topic.follow')
                                            }
                                            onClick={() => void setTopicFollowed(!topic.followedByCurrentUser)}
                                        >
                                            {topic.followedByCurrentUser ? <BellOffIcon /> : <BellIcon />}
                                        </Button>
                                    )}
                                    {canModerate && topic && (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="icon-sm"
                                                    title={t('product.community.topic.actions')}
                                                >
                                                    <MoreHorizontalIcon />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => void setTopicPinned(!topicPinned)}>
                                                    {topicPinned ? <PinOffIcon /> : <PinIcon />}
                                                    {topicPinned
                                                        ? t('product.community.topic.unpin')
                                                        : t('product.community.topic.pin')}
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => void setTopicLocked(!topicLocked)}>
                                                    {topicLocked ? <LockOpenIcon /> : <LockIcon />}
                                                    {topicLocked
                                                        ? t('product.community.topic.unlock')
                                                        : t('product.community.topic.lock')}
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem onClick={() => setMoveDialogOpen(true)}>
                                                    <MoveRightIcon />
                                                    {t('product.community.topic.move')}
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}
                                    {canEditTopic && (
                                        <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            title={t('product.community.topic.edit')}
                                            onClick={() => setTopicDialogOpen(true)}
                                        >
                                            <PencilIcon />
                                        </Button>
                                    )}
                                    {canDeleteTopic && (
                                        <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            title={t('product.community.topic.delete')}
                                            onClick={() => setTopicDeleteOpen(true)}
                                        >
                                            <Trash2Icon />
                                        </Button>
                                    )}
                                </div>
                            </div>
                            {topic ? (
                                <div>
                                    <Markdown className="text-base leading-7" syntaxHighlighting>
                                        {topic.message}
                                    </Markdown>
                                    <AttachmentList
                                        markdown={topic.message}
                                        attachments={extractStorageResources(topic.attachments)}
                                    />
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-2/3" />
                                </div>
                            )}
                        </div>
                    </div>
                </CardHeader>
            </Card>
        )
    }
)
